import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { RxCross2, RxDashboard } from 'react-icons/rx'
import { Button } from '~/@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/@/components/ui/dialog'
import Typography from '~/@/components/ui/typography'
import { cn } from '~/@/lib/utils'
import { useMoveWorkspaceMutation, ViewableObjectType } from '~/common/generated/apollo-operations'
import { useOrgProjects } from '~/common/hooks/useOrgProjects'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { useDossSnackbar } from '../shared/DossSnackbar'

interface MoveWorkspaceToProjectDialogProps {
  open: boolean
  onClose: () => void
  objectId: string
  objectType: ViewableObjectType
  oldParentWorkspaceId: string
}

/**
 * Dialog to move a workspace block to another project.
 * The block is moved to the selected project's root workspace.
 * User selects a project, then confirms with Save (or cancels).
 */
export function MoveWorkspaceToProjectDialog({
  open,
  onClose,
  objectId,
  objectType,
  oldParentWorkspaceId,
}: MoveWorkspaceToProjectDialogProps) {
  const { t } = useTypedTranslation()
  const { showError, showSuccess } = useDossSnackbar()
  const [selectedRootWorkspaceId, setSelectedRootWorkspaceId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  // Root workspaces, already sorted by order and loaded once for the whole app
  const { projects: sortedProjects, projectsLoading } = useOrgProjects()
  const [moveWorkspace] = useMoveWorkspaceMutation()

  const handleClose = () => {
    if (!isLoading) {
      setSelectedRootWorkspaceId(null)
      onClose()
    }
  }

  const handleSave = async () => {
    if (!selectedRootWorkspaceId) {
      return
    }
    setIsLoading(true)
    try {
      await moveWorkspace({
        variables: {
          input: {
            objectId,
            objectType,
            newParentWorkspaceId: selectedRootWorkspaceId,
            oldParentWorkspaceId,
          },
        },
      })
      showSuccess(t('workspace.move_to_project_success'))
      handleClose()
      // Refresh the page to update the left nav context
      window.location.reload()
    } catch {
      showError(t('workspace.move_to_project_error'))
    } finally {
      setIsLoading(false)
    }
  }

  const canSave = !!selectedRootWorkspaceId && !isLoading

  return (
    <Dialog onOpenChange={(isOpen) => !isOpen && handleClose()} open={open}>
      <DialogContent
        className="flex max-h-[80vh] w-full max-w-lg flex-col gap-0 border-none p-0"
        hideCloseButton
      >
        <DialogHeader className="rounded-t-lg border-gray-3 bg-gray-3">
          <DialogTitle className="flex items-center justify-between rounded-t-lg border-gray-5 bg-gray-3 px-6 py-3">
            <h3 className="font-semibold">{t('workspace.move_to_project')}</h3>
            <DialogClose asChild>
              <Button disabled={isLoading} onClick={handleClose} size="smallIcon" variant="ghost">
                <RxCross2 className="size-5" />
              </Button>
            </DialogClose>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-2 overflow-auto p-6">
          {projectsLoading ? (
            <div className="flex flex-1 items-center justify-center py-12">
              <Loader2 className="size-8 animate-spin text-gray-6" />
            </div>
          ) : (
            sortedProjects.map((project) => {
              const rootWorkspaceId = project.id
              const isCurrentLocation = rootWorkspaceId === oldParentWorkspaceId
              const isSelected = selectedRootWorkspaceId === rootWorkspaceId
              return (
                <button
                  key={project.id}
                  className={cn(
                    'border-gray-4 hover:border-gray-5 hover:bg-gray-2 flex items-center gap-3 rounded border bg-white p-2 px-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    isSelected && 'border-gray-6 bg-gray-2 ring-1 ring-gray-6',
                  )}
                  disabled={isCurrentLocation}
                  onClick={() => setSelectedRootWorkspaceId(rootWorkspaceId)}
                  type="button"
                >
                  <RxDashboard className="size-4 shrink-0 text-gray-7" />
                  <Typography className="flex-1 text-gray-9" variant="body2">
                    {project.name}
                  </Typography>
                  {isCurrentLocation && (
                    <Typography className="text-xs text-gray-6" variant="caption">
                      {t('workspace.current_project')}
                    </Typography>
                  )}
                </button>
              )
            })
          )}
        </div>

        <DialogFooter className="flex w-full items-center justify-end gap-2 border-t border-gray-4 px-6 py-3">
          <Button disabled={isLoading} onClick={handleClose} size="sm" variant="outline">
            <div className="text-sm">{t('common.actions.cancel')}</div>
          </Button>
          <Button
            className="relative"
            disabled={!canSave}
            onClick={handleSave}
            size="sm"
            variant="secondary"
          >
            <div className={cn('text-sm', isLoading && 'invisible')}>
              {t('common.actions.save')}
            </div>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="size-4 animate-spin" />
              </div>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
